<template>
    <div class="phone-input-wrapper">
        <label :for="inputId" class="form-label">
            {{ label ?? '' }}
            <span v-if="required && label" class="text-danger">*</span>
        </label>
        <div class="row g-2">
            <div class="col-5">
                <div class="input-group">
                    <span class="input-group-text">
                        <i class="bi bi-telephone"></i>
                    </span>
                    <select 
                        class="form-select country" 
                        v-model="selectedIsoCode"
                        :disabled="disabled"
                    >
                        <option v-for="country in countryCodes" :key="country.iso_code" :value="country.iso_code">
                            {{ country.name === 'United Kingdom' ? 'UK' : country.name }} (+{{ country.phone_code }}) 
                        </option>
                    </select>
                </div>
            </div>
            <div class="col-7">
                <div class="input-group">
                    <input 
                        type="tel" 
                        class="form-control" 
                        :id="inputId"
                        :value="phoneNumber"
                        :placeholder="placeholder"
                        :disabled="disabled"
                        @input="handlePhoneInput"
                        @blur="handleBlur"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api from "@/services/api";

export default {
    name: 'PhoneInput',
    props: {
        phoneNumber: {
            type: String,
            default: ''
        },
        countryCode: {
            type: String,
            default: ''
        },
        label: {
            type: String,
            default: ''
        },
        placeholder: {
            type: String,
            default: '07234 123 456'
        },
        required: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    emits: ['update:phoneNumber', 'update:countryCode', 'blur'],
    data() {
        return {
            countryCodes: [],
            selectedIsoCode: '',
            inputId: `phone-input-${Math.random().toString(36).substr(2, 9)}`
        };
    },
    computed: {
        currentPhoneCode() {
            if (!this.selectedIsoCode || !this.countryCodes.length) {
                return '';
            }
            const selectedCountry = this.countryCodes.find(country => country.iso_code === this.selectedIsoCode);
            return selectedCountry ? selectedCountry.phone_code : '';
        }
    },
    watch: {
        countryCodes(newCodes) {
            if (newCodes.length > 0 && !this.selectedIsoCode) {
                this.selectedIsoCode = newCodes[0].iso_code;
            }

            // Auto-parse international phone number when country codes are loaded
            if (newCodes.length > 0 && this.phoneNumber && this.phoneNumber.startsWith('+')) {
                this.parseAndSetFromInternational(this.phoneNumber);
            }
        },
        selectedIsoCode() {
            const selectedCountry = this.countryCodes.find(country => country.iso_code === this.selectedIsoCode);
            this.$emit('update:countryCode', selectedCountry ? selectedCountry.phone_code : '');
        },
        phoneNumber(newPhoneNumber) {
            // Auto-parse international numbers when prop changes
            if (newPhoneNumber && newPhoneNumber.startsWith('+') && this.countryCodes.length) {
                this.parseAndSetFromInternational(newPhoneNumber);
            }
        }
    },
    methods: {
        async fetchCountryCodes() {
            try {
                const response = await api.post('/api/v1/userAdmin/countryCodes');
                this.countryCodes = response.data;
            } catch (error) {
                // Fallback to basic country codes if API fails
                this.countryCodes = [
                    { iso_code: 'GB', name: 'United Kingdom', phone_code: '44' },
                    { iso_code: 'IE', name: 'Ireland', phone_code: '353' }
                ];
            }
        },
        handlePhoneInput(event) {
            this.$emit('update:phoneNumber', event.target.value);
        },
        handleBlur(event) {
            this.$emit('blur', event);
        },
        parseInternationalPhone(fullNumber) {
            // Public method to parse international phone numbers using actual country data
            if (!fullNumber || !this.countryCodes.length) {
                return { countryCode: '', localNumber: fullNumber || '' };
            }
            
            const cleanNumber = fullNumber.replace(/\s/g, '');
            
            // If doesn't start with +, assume it's already a local number
            if (!cleanNumber.startsWith('+')) {
                return { countryCode: '', localNumber: cleanNumber };
            }
            
            // Sort country codes by length (longest first) for accurate matching
            const sortedCodes = this.countryCodes
                .slice()
                .sort((a, b) => b.phone_code.length - a.phone_code.length);
            
            for (const country of sortedCodes) {
                const prefix = '+' + country.phone_code;
                if (cleanNumber.startsWith(prefix)) {
                    return {
                        countryCode: country.phone_code,
                        localNumber: cleanNumber.substring(prefix.length)
                    };
                }
            }
            
            // If no match found, return as local number
            return { countryCode: '', localNumber: cleanNumber.substring(1) };
        },
        
        parseAndSetFromInternational(internationalNumber) {
            // Helper method to parse international number and update component state
            const phoneData = this.parseInternationalPhone(internationalNumber);
            
            if (phoneData.countryCode) {
                // Find matching country and set dropdown
                const matchingCountry = this.countryCodes.find(
                    country => country.phone_code === phoneData.countryCode
                );
                if (matchingCountry) {
                    this.selectedIsoCode = matchingCountry.iso_code;
                }
            }
            
            // Emit the parsed local number back to parent
            this.$emit('update:phoneNumber', phoneData.localNumber);
            this.$emit('update:countryCode', phoneData.countryCode);
        },

        getFormattedPhoneForSubmission() {
            // Public method to get formatted phone for API submission
            let formattedPhone = this.phoneNumber.replace(/\s/g, '').trim();
            if (formattedPhone.startsWith('0')) {
                formattedPhone = formattedPhone.substring(1);
            }
            return formattedPhone;
        }
    },
    mounted() {
        this.fetchCountryCodes();
        
        // Initialize country code if provided
        if (this.countryCode) {
            const matchingCountry = this.countryCodes.find(country => country.phone_code === this.countryCode);
            if (matchingCountry) {
                this.selectedIsoCode = matchingCountry.iso_code;
            }
        }
    }
};
</script>

<style scoped lang="scss">
.country {
    font-size: 16px;
    padding: 0 28px 0 8px;
}

.phone-input-wrapper {
    .form-label {
        font-weight: 500;
        color: #495057;
        margin-bottom: 0.5rem;
    }
    
    
    .form-control,
    .form-select {
        &:disabled {
            cursor: not-allowed;
            background-color: #f8f9fa;
        }
        
        &:focus {
            border-color: #86b7fe;
            box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
        }
    }
}
</style>